import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Wallet, CreditCard, Plus, Loader2 } from "lucide-react";
import { fmtEuro } from "@/lib/wallet";

/**
 * Confirmation dialog shown when the user hits "Buy" on a marketplace item.
 * Behaviour:
 *   - If wallet has enough → default action pays from wallet, secondary
 *     action falls back to Stripe Checkout.
 *   - If wallet balance is short → primary action redirects to the Wallet
 *     page to top up, secondary action falls back to Stripe Checkout.
 * On confirm, calls one of the two supplied handlers.
 */
export default function PayFromWalletDialog({
  open, onOpenChange, product, wallet, onPayWallet, onPayCard,
}) {
  const [busy, setBusy] = useState(null);

  // Reset the busy indicator whenever the dialog opens on a new product so
  // clicking "Buy" a second time (after a prior cancel) is never disabled.
  useEffect(() => {
    if (open) setBusy(null);
  }, [open, product?.product_id]);

  if (!product) return null;

  const price = product.price_cents || 0;
  const balance = wallet?.balance_cents || 0;
  const enough = balance >= price;
  const shortBy = Math.max(0, price - balance);

  const doWallet = async () => {
    setBusy("wallet");
    try { await onPayWallet(product); onOpenChange(false); }
    catch { /* handler shows toast */ }
    finally { setBusy(null); }
  };
  const doCard = async () => {
    setBusy("card");
    try { await onPayCard(product); onOpenChange(false); }
    catch { /* handler shows toast */ }
    finally { setBusy(null); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="pay-from-wallet-dialog"
        className="bg-[#121212] border-[#27272A] text-[#F3F4F6] max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="font-heading">Pay for &quot;{product.name}&quot;</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="rounded-2xl bg-[#1E1E1E] border border-[#27272A] p-4 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#6B7280]">Price</div>
              <div className="text-2xl font-bold tabular-nums">{fmtEuro(price)}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider text-[#6B7280]">Wallet</div>
              <div className={`text-lg font-semibold tabular-nums ${enough ? "text-[#10B981]" : "text-[#F59E0B]"}`}>
                {fmtEuro(balance)}
              </div>
            </div>
          </div>

          {enough ? (
            <div className="rounded-xl bg-[#10B981]/10 border border-[#10B981]/30 px-4 py-3 text-sm text-[#10B981]">
              Enough in your wallet — instant purchase, no card needed.
            </div>
          ) : (
            <div className="rounded-xl bg-[#F59E0B]/10 border border-[#F59E0B]/30 px-4 py-3 text-sm text-[#F59E0B]">
              Wallet is short by <b className="tabular-nums">{fmtEuro(shortBy)}</b>. Top up or pay directly with your card.
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2 flex-col sm:flex-row">
          {enough ? (
            <>
              <Button
                variant="ghost"
                data-testid="pay-card-fallback-btn"
                onClick={doCard}
                disabled={!!busy}
                className="rounded-full text-[#9CA3AF] hover:bg-[#1E1E1E]"
              >
                <CreditCard className="h-4 w-4 mr-2" /> Pay with card instead
              </Button>
              <Button
                data-testid="pay-wallet-btn"
                onClick={doWallet}
                disabled={!!busy}
                className="rounded-full bg-[#10B981] hover:bg-[#059669] text-white"
              >
                {busy === "wallet"
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <><Wallet className="h-4 w-4 mr-2" /> Pay {fmtEuro(price)} from wallet</>}
              </Button>
            </>
          ) : (
            <>
              <Button
                data-testid="pay-card-btn"
                onClick={doCard}
                disabled={!!busy}
                className="rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
              >
                {busy === "card"
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <><CreditCard className="h-4 w-4 mr-2" /> Pay {fmtEuro(price)} with card</>}
              </Button>
              <TopupLink onClose={() => onOpenChange(false)} />
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TopupLink({ onClose }) {
  const navigate = useNavigate();
  return (
    <Button
      variant="ghost"
      data-testid="pay-topup-btn"
      onClick={() => { onClose(); navigate("/wallet"); }}
      className="rounded-full text-[#F3F4F6] hover:bg-[#1E1E1E] border border-[#27272A]"
    >
      <Plus className="h-4 w-4 mr-2" /> Top up wallet
    </Button>
  );
}
